import React, { useState, useEffect } from "react";
import {
  Container, CardContent, Typography, TextField, Button, Grid, Box,
  FormControl, InputLabel, MenuItem, Select, Alert, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Tooltip,
  CircularProgress, Card, Divider, Stack, InputAdornment, Snackbar,
  Avatar, Breadcrumbs, Tabs, Tab, Fade, Zoom, Collapse,
  alpha, useTheme
} from "@mui/material";
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SortIcon from '@mui/icons-material/Sort';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LaunchIcon from '@mui/icons-material/Launch';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.primary.main,
  },
}));

const VideoThumbnail = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '12px 12px 0 0',
  overflow: 'hidden',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  height: 180,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  '&:hover': {
    '& .play-overlay': {
      backgroundColor: alpha('#000', 0.5),
    },
    '& .play-icon': {
      transform: 'scale(1.2)',
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(102,126,234,0.2) 0%, rgba(118,75,162,0.2) 100%)',
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  borderRadius: 8,
  fontWeight: 600,
  borderWidth: 2,
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  margin: '0 4px',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

const VideoImageButton = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  cursor: 'pointer',
  '&:hover': {
    '& .play-overlay': {
      opacity: 1,
    }
  },
}));

function TrainingVideoManagement() {
  const theme = useTheme();
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [formMode, setFormMode] = useState("add");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('recent');
  const [expandedVideo, setExpandedVideo] = useState(null);

  const [videoForm, setVideoForm] = useState({
    title: "",
    videoUrl: "",
    skillCategory: "",
    skillContent: "",
    description: ""
  });

  const [manualCategory, setManualCategory] = useState("");
  const [manualContent, setManualContent] = useState("");
  const [showManualCategory, setShowManualCategory] = useState(false);
  const [showManualContent, setShowManualContent] = useState(false);

  const skillOptions = [
    "Process Management",
    "People Management",
    "Interpersonal Skills",
    "Communication Skills",
    "Managing Skills",
    "Technical Skills",
    "Cultural Skills",
    "Business Skills",
    "Accounting Skills",
    "Industrial Certificates"
  ];

  const skillContents = {
    "Process Management": ["Workflow Optimization", "Lean Six Sigma", "Agile Methodology", "Process Automation"],
    "People Management": ["Team Leadership", "Conflict Resolution", "Performance Evaluation", "Talent Development"],
    "Interpersonal Skills": ["Empathy Building", "Active Listening", "Networking Strategies", "Emotional Intelligence"],
    "Communication Skills": ["Business Writing", "Presentation Skills", "Negotiation Techniques", "Public Speaking"],
    "Managing Skills": ["Time Management", "Risk Management", "Decision Making", "Strategic Planning"],
    "Technical Skills": ["Python Programming", "Cloud Computing", "Data Analysis", "Machine Learning", "Cybersecurity"],
    "Cultural Skills": ["Diversity Training", "Cross-Cultural Communication", "Inclusive Leadership", "Global Mindset"],
    "Business Skills": ["Market Analysis", "Strategic Planning", "Entrepreneurship", "Financial Management"],
    "Accounting Skills": ["Financial Reporting", "Tax Planning", "Budgeting", "Audit Procedures"],
    "Industrial Certificates": ["AWS Certified", "PMP Certification", "ISO Compliance", "Six Sigma Certification"]
  };

  // Stats for dashboard
  const stats = {
    totalVideos: videos.length,
    categories: [...new Set(videos.map(v => v.skillCategory))].length,
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  useEffect(() => {
    filterAndSearchVideos();
  }, [videos, searchTerm, filterCategory, sortBy]);

  useEffect(() => {
    if (success) {
      setSnackbarOpen(true);
      const timer = setTimeout(() => {
        setSuccess("");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (!openDialog) {
      setDialogError("");
      setAiGenerating(false);
      setManualCategory("");
      setManualContent("");
      setShowManualCategory(false);
      setShowManualContent(false);
    }
  }, [openDialog]);

  const filterAndSearchVideos = () => {
    let filtered = [...videos];

    if (filterCategory) {
      filtered = filtered.filter(video => video.skillCategory === filterCategory);
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(video =>
        video.title.toLowerCase().includes(search) ||
        video.skillCategory.toLowerCase().includes(search) ||
        video.skillContent.toLowerCase().includes(search) ||
        (video.description && video.description.toLowerCase().includes(search))
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'category':
          return a.skillCategory.localeCompare(b.skillCategory);
        case 'recent':
        default:
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });

    setFilteredVideos(filtered);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'skillCategory') {
      if (value === 'manual') {
        setShowManualCategory(true);
        setShowManualContent(false);
        setVideoForm(prev => ({
          ...prev,
          skillCategory: '',
          skillContent: ''
        }));
      } else {
        setShowManualCategory(false);
        setVideoForm(prev => ({
          ...prev,
          [name]: value,
          skillContent: ''
        }));
      }
    } else if (name === 'skillContent') {
      if (value === 'manual') {
        setShowManualContent(true);
        setVideoForm(prev => ({
          ...prev,
          skillContent: ''
        }));
      } else {
        setShowManualContent(false);
        setVideoForm(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setVideoForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (dialogError) setDialogError("");
  };

  const handleManualCategoryChange = (e) => {
    const value = e.target.value;
    setManualCategory(value);
    setVideoForm(prev => ({
      ...prev,
      skillCategory: value
    }));
  };

  const handleManualContentChange = (e) => {
    const value = e.target.value;
    setManualContent(value);
    setVideoForm(prev => ({
      ...prev,
      skillContent: value
    }));
  };

  const handleVideoAction = (video, mode) => {
    if (mode === "edit") {
      // Set form values from video data
      setVideoForm({
        title: video.title,
        videoUrl: video.videoUrl,
        skillCategory: video.skillCategory,
        skillContent: video.skillContent,
        description: video.description || ""
      });
      setCurrentVideo(video);
      setFormMode("edit");
      setOpenDialog(true);
    } else if (mode === "delete") {
      setCurrentVideo(video);
      setFormMode("delete");
      setOpenDialog(true);
    }
  };

  const handleOpenVideo = (videoUrl) => {
    if (videoUrl) {
      window.open(videoUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const validateVideoUrl = (url) => {
    if (!url) return false;
    const patterns = [
      /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/,
      /^(https?:\/\/)?(www\.)?vimeo\.com\/.+$/,
      /^(https?:\/\/).+\.(mp4|webm|ogg)$/,
      /^https?:\/\/.+/
    ];
    return patterns.some(pattern => pattern.test(url));
  };

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Authentication required. Please log in.");
        setLoading(false);
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(`${API_URL}/api/training-videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setVideos(response.data);
      setError("");
    } catch (error) {
      //console.error("Error fetching videos:", error);
      if (error.response?.status === 401) {
        setError("Session expired. Please log in again.");
      } else if (error.response?.status === 404) {
        setError("Training videos endpoint not found. Please contact support.");
      } else {
        setError(error.response?.data?.message || "Failed to fetch training videos. Please try again.");
      }
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const addVideo = async () => {
    if (!videoForm.title.trim() || !videoForm.videoUrl.trim() || 
        !videoForm.skillCategory || !videoForm.skillContent) {
      setDialogError("Please fill in all required fields.");
      return;
    }

    if (!validateVideoUrl(videoForm.videoUrl)) {
      setDialogError("Please enter a valid video URL.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      await axios.post(
        `${API_URL}/api/training-videos`,
        videoForm,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      setSuccess("Training video added successfully!");
      setOpenDialog(false);
      resetForm();
      fetchVideos();
    } catch (error) {
      //console.error("Error adding video:", error);
      setDialogError(error.response?.data?.message || "Failed to add training video. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateVideo = async () => {
    if (!videoForm.title.trim() || !videoForm.videoUrl.trim() || 
        !videoForm.skillCategory || !videoForm.skillContent) {
      setDialogError("Please fill in all required fields.");
      return;
    }

    if (!validateVideoUrl(videoForm.videoUrl)) {
      setDialogError("Please enter a valid video URL.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      await axios.put(
        `${API_URL}/api/training-videos/${currentVideo.id}`,
        videoForm,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      setSuccess("Training video updated successfully!");
      setOpenDialog(false);
      resetForm();
      fetchVideos();
    } catch (error) {
      //console.error("Error updating video:", error);
      setDialogError(error.response?.data?.message || "Failed to update training video. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteVideo = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      await axios.delete(
        `${API_URL}/api/training-videos/${currentVideo.id}`,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      setSuccess("Training video deleted successfully!");
      setOpenDialog(false);
      resetForm();
      fetchVideos();
    } catch (error) {
      //console.error("Error deleting video:", error);
      setError(error.response?.data?.message || "Failed to delete training video. Please try again.");
      setOpenDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setVideoForm({ title: "", videoUrl: "", skillCategory: "", skillContent: "", description: "" });
    setManualCategory("");
    setManualContent("");
    setShowManualCategory(false);
    setShowManualContent(false);
    setCurrentVideo(null);
    setDialogError("");
    setFormMode("add");
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Process Management": "primary",
      "People Management": "secondary",
      "Interpersonal Skills": "success",
      "Communication Skills": "info",
      "Managing Skills": "warning",
      "Technical Skills": "error",
      "Cultural Skills": "primary",
      "Business Skills": "secondary",
      "Accounting Skills": "success",
      "Industrial Certificates": "info"
    };
    return colors[category] || "default";
  };

  const generateAIVideoSuggestions = async () => {
    if (!videoForm.skillCategory || !videoForm.skillContent) {
      setAlert({
        open: true,
        message: "Please select both skill category and content before using AI generation.",
        severity: "warning"
      });
      return;
    }

    setDialogError("");
    setAiGenerating(true);

    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const response = await axios.post(
        `${API_URL}/api/training-videos/ai-generate`,
        {
          skillCategory: videoForm.skillCategory,
          skillContent: videoForm.skillContent,
          description: videoForm.description || "",
        },{
         headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (response.data.suggestions && response.data.suggestions.length > 0) {
        const suggestion = response.data.suggestions[0];
        setVideoForm(prev => ({
          ...prev,
          videoUrl: suggestion.videoUrl || prev.videoUrl,
          title: suggestion.title || prev.title,
          description: suggestion.description || prev.description
        }));
        
        setDialogError("");
        setAlert({
          open: true,
          message: "✨ AI video suggestions loaded successfully!",
          severity: "success"
        });
      } else {
        setAlert({
          open: true,
          message: "No AI suggestions available at this time. Please enter details manually.",
          severity: "warning"
        });
      }
    } catch (err) {
      //console.error("Error fetching AI suggestions:", err);
      const errorData = err.response?.data;

      // 1. IF PLAN DOES NOT INCLUDE AI (e.g., Basic Plan with 0 tokens)
      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        setAlert({
          open: true,
          message: errorData.message || "AI features are not included in your current plan. Please upgrade to unlock AI.",
          severity: "warning"
        });
      } 
      // 2. IF TOKENS ARE EXHAUSTED
      else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        setAlert({
          open: true,
          message: `⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`,
          severity: "warning"
        });
      } 
      else {
        let errorMessage = "Failed to generate AI video suggestions. Please try again.";
        if (errorData?.message) {
          errorMessage = errorData.message;
        }
        setAlert({
          open: true,
          message: errorMessage,
          severity: "error"
        });
      }
    } finally {
      setAiGenerating(false);
    }
  };

  const getYoutubeThumbnail = (url) => {
    if (!url) return '';
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? `https://img.youtube.com/vi/${match[7]}/hqdefault.jpg` : '';
  };

  const StatCard = ({ icon, title, value, color }) => (
    <Card sx={{ borderRadius: 3, p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: color, color: 'white' }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );

  const VideoCard = ({ video }) => (
    <Zoom in={true}>
      <StyledCard>
        <VideoThumbnail onClick={() => handleOpenVideo(video.videoUrl)}>
          <VideoImageButton>
            <Box
              component="img"
              src={getYoutubeThumbnail(video.videoUrl)}
              alt={video.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.8,
              }}
              onError={(e) => {
                e.target.src = '';
                e.target.style.display = 'none';
              }}
            />
            <Box
              className="play-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha('#000', 0.3),
                transition: 'all 0.3s ease',
              }}
            >
              <PlayCircleOutlineIcon 
                className="play-icon"
                sx={{ 
                  fontSize: 60, 
                  color: 'white',
                  transition: 'transform 0.3s ease'
                }} 
              />
            </Box>
          </VideoImageButton>
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              display: 'flex',
              gap: 1,
            }}
          >
            <Chip
              label={video.skillCategory}
              size="small"
              color={getCategoryColor(video.skillCategory)}
              sx={{
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
              }}
            />
          </Box>
        </VideoThumbnail>
        <CardContent sx={{ p: 3 }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              minHeight: '3em',
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.primary.main,
              }
            }}
            onClick={() => handleOpenVideo(video.videoUrl)}
          >
            {video.title}
          </Typography>
          
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              icon={<CategoryIcon fontSize="small" />}
              label={video.skillContent}
              size="small"
              variant="outlined"
              color="primary"
            />
          </Stack>

          {video.description && (
            <Collapse in={expandedVideo === video.id}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {video.description}
              </Typography>
            </Collapse>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Box
  sx={{
    display: "flex",
    gap: 2,
    alignItems: "center",
  }}
>
  {/* Play */}
  <Tooltip title="Play Video">
    <ActionButton
      onClick={() => handleOpenVideo(video.videoUrl)}
      sx={{
        width: 45,
        height: 45,
      }}
    >
      <PlayCircleOutlineIcon sx={{ fontSize: 28 }} />
    </ActionButton>
  </Tooltip>

  {/* Edit */}
  <Tooltip title="Edit Video">
    <ActionButton
      onClick={() => handleVideoAction(video, "edit")}
      sx={{
        width: 45,
        height: 45,
      }}
    >
      <EditIcon sx={{ fontSize: 24 }} />
    </ActionButton>
  </Tooltip>

  {/* Delete */}
  <Tooltip title="Delete Video">
    <ActionButton
      color="error"
      onClick={() => handleVideoAction(video, "delete")}
      sx={{
        width: 45,
        height: 45,
      }}
    >
      <DeleteIcon sx={{ fontSize: 24 }} />
    </ActionButton>
  </Tooltip>
</Box>
            
           
          </Box>
        </CardContent>
      </StyledCard>
    </Zoom>
  );

  const VideoListRow = ({ video }) => (
    <Fade in={true}>
      <StyledCard sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                height: 100,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                cursor: 'pointer',
              }}
              onClick={() => handleOpenVideo(video.videoUrl)}
            >
              <Box
                component="img"
                src={getYoutubeThumbnail(video.videoUrl)}
                alt={video.title}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  e.target.src = '';
                  e.target.style.display = 'none';
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha('#000', 0.3),
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                  }
                }}
              >
                <PlayCircleOutlineIcon sx={{ fontSize: 30, color: 'white' }} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography 
              variant="subtitle1" 
              fontWeight={600} 
              gutterBottom
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                }
              }}
              onClick={() => handleOpenVideo(video.videoUrl)}
            >
              {video.title}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <CategoryChip
                label={video.skillCategory}
                size="small"
                color={getCategoryColor(video.skillCategory)}
                variant="outlined"
              />
              <Chip
                label={video.skillContent}
                size="small"
                variant="outlined"
              />
            </Stack>
            {video.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {video.description}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayCircleOutlineIcon />}
              onClick={() => handleOpenVideo(video.videoUrl)}
              sx={{ borderRadius: 2 }}
            >
              Play Video
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Tooltip title="Edit">
                <ActionButton
                  size="small"
                  onClick={() => handleVideoAction(video, "edit")}
                >
                  <EditIcon fontSize="small" />
                </ActionButton>
              </Tooltip>
              <Tooltip title="Delete">
                <ActionButton
                  size="small"
                  color="error"
                  onClick={() => handleVideoAction(video, "delete")}
                >
                  <DeleteIcon fontSize="small" />
                </ActionButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </StyledCard>
    </Fade>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>

     

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Add Videos 
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and organize your organization's training content
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => { 
              setFormMode("add"); 
              setOpenDialog(true); 
              resetForm();
            }}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              borderRadius: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              },
            }}
          >
            Add New Video
          </Button>
        </Stack>
  



      {/* Control Panel */}
      <Card sx={{ mb: 3, p: 2, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 }
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={filterCategory}
                label="Filter by Category"
                onChange={(e) => setFilterCategory(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {skillOptions.map((skill) => (
                  <MenuItem key={skill} value={skill}>{skill}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="recent">Most Recent</MenuItem>
                <MenuItem value="title">Title A-Z</MenuItem>
                <MenuItem value="category">Category</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <Tooltip title="Grid View">
                <IconButton
                  onClick={() => setViewMode('grid')}
                  color={viewMode === 'grid' ? 'primary' : 'default'}
                >
                  <ViewModuleIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="List View">
                <IconButton
                  onClick={() => setViewMode('list')}
                  color={viewMode === 'list' ? 'primary' : 'default'}
                >
                  <ViewListIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                {filteredVideos.length} videos
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Videos Display */}
      {loading && videos.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              Loading training videos...
            </Typography>
          </Stack>
        </Box>
      ) : filteredVideos.length === 0 ? (
        <Card sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
          <VideoLibraryIcon sx={{ fontSize: 80, color: 'grey.300', mb: 3 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            {searchTerm || filterCategory ? 'No videos found' : 'No training videos available'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || filterCategory 
              ? 'Try adjusting your search or filters'
              : 'Start by adding your first training video'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { 
              setFormMode("add"); 
              setOpenDialog(true); 
              resetForm();
            }}
          >
            Add Your First Video
          </Button>
        </Card>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {filteredVideos.map((video) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
              <VideoCard video={video} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          {filteredVideos.map((video) => (
            <VideoListRow key={video.id} video={video} />
          ))}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog && formMode !== "delete"} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 2 }}>
          {formMode === "add" ? "Add New Training Video" : "Edit Training Video"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError("")}>
              {dialogError}
            </Alert>
          )}
          {aiGenerating && (
            <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>
              Generating AI suggestions... Please wait.
            </Alert>
          )}
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Video Title"
                name="title"
                value={videoForm.title}
                onChange={handleChange}
                required
                placeholder="Enter a descriptive title for the training video"
                helperText="Provide a clear, concise title"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Video URL"
                name="videoUrl"
                value={videoForm.videoUrl}
                onChange={handleChange}
                required
                placeholder="https://www.youtube.com/watch?v=..."
                helperText="Enter a valid video URL (YouTube, Vimeo, or direct link)"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Skill Category</InputLabel>
                <Select
                  name="skillCategory"
                  value={showManualCategory ? 'manual' : videoForm.skillCategory}
                  label="Skill Category"
                  onChange={handleChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Select Category</MenuItem>
                  {skillOptions.map((skill) => (
                    <MenuItem key={skill} value={skill}>{skill}</MenuItem>
                  ))}
                  <MenuItem value="manual">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EditIcon fontSize="small" />
                      Enter Manually
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              {showManualCategory && (
                <TextField
                  fullWidth
                  label="Enter Custom Category"
                  value={manualCategory}
                  onChange={handleManualCategoryChange}
                  required
                  helperText="Enter your own skill category"
                  sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Skill Content</InputLabel>
                <Select
                  name="skillContent"
                  value={showManualContent ? 'manual' : videoForm.skillContent}
                  label="Skill Content"
                  onChange={handleChange}
                  disabled={!videoForm.skillCategory}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Select Content</MenuItem>
                  {videoForm.skillCategory && skillContents[videoForm.skillCategory]?.map((content) => (
                    <MenuItem key={content} value={content}>{content}</MenuItem>
                  ))}
                  <MenuItem value="manual">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EditIcon fontSize="small" />
                      Enter Manually
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              {showManualContent && (
                <TextField
                  fullWidth
                  label="Enter Custom Content"
                  value={manualContent}
                  onChange={handleManualContentChange}
                  required
                  helperText="Enter your own skill content"
                  sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                name="description"
                value={videoForm.description}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Add additional details about this training video..."
                helperText={`${videoForm.description.length}/500 characters`}
                inputProps={{ maxLength: 500 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={handleCloseDialog} disabled={loading || aiGenerating}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Use AI to generate video suggestions based on selected skills and description">
            <span>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={aiGenerating ? <CircularProgress size={16} /> : <SmartToyIcon />}
                onClick={generateAIVideoSuggestions}
                disabled={loading || aiGenerating || !videoForm.skillCategory || !videoForm.skillContent}
                sx={{ borderRadius: 2 }}
              >
                AI Suggest
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            onClick={formMode === "add" ? addVideo : updateVideo}
            disabled={loading || aiGenerating}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
            sx={{ borderRadius: 2 }}
          >
            {formMode === "add" ? "Add Video" : "Update Video"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={openDialog && formMode === "delete"} 
        onClose={handleCloseDialog}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Deletion</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete the training video:
          </Typography>
          <Typography fontWeight="600" sx={{ mt: 1, color: 'error.main' }}>
            "{currentVideo?.title}"
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseDialog} disabled={loading} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={deleteVideo}
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
            sx={{ borderRadius: 2 }}
          >
            Delete Video
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {success}
        </Alert>
      </Snackbar>

      {/* Top-Center Floating Alert Snackbar */}
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={() => setAlert(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          onClose={() => setAlert(prev => ({ ...prev, open: false }))} 
          severity={alert.severity}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TrainingVideoManagement;